import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../config/api-client';
import { queryKeys } from '../config/query-keys';
import type { UserProfile } from '../types';

export function useProfileQuery(options?: { readonly enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.profile,
    queryFn: async () => {
      const result = await apiClient.get<{ data: UserProfile }>('/users/profile');
      return result.data.data;
    },
    retry: (failureCount, error) => {
      const status = (error as { statusCode?: number }).statusCode;
      // 404 means "no profile yet" — don't retry, the caller will route
      // the user into onboarding instead.
      if (status === 404) return false;
      return failureCount < 2;
    },
    enabled: options?.enabled ?? true,
  });
}

interface CreateProfileInput {
  readonly primaryLanguage: string;
  readonly knownLanguages: string[];
  readonly displayName?: string;
  readonly homeCountry?: string;
  readonly currentCountry?: string;
  readonly city?: string;
}

export function useCreateProfileMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateProfileInput) => {
      const result = await apiClient.post<{ data: UserProfile }>('/users/profile', input);
      return result.data.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.profile, data);
    },
  });
}

export function useUpdateProfileMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (updates: Partial<UserProfile>) => {
      const result = await apiClient.patch<{ data: UserProfile }>('/users/profile', updates);
      return result.data.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.profile, data);
    },
  });
}

interface ProfileCompletionStatus {
  readonly completionPercentage: number;
  readonly missingFields: string[];
  readonly nextPrompt: string | null;
}

export function useProfileCompletionQuery(options?: { readonly enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.profileCompletion,
    queryFn: async () => {
      const result = await apiClient.get<{ data: ProfileCompletionStatus }>('/users/profile/completion');
      return result.data.data;
    },
    retry: (failureCount, error) => {
      const status = (error as { statusCode?: number }).statusCode;
      if (status === 404) return false;
      return failureCount < 2;
    },
    enabled: options?.enabled ?? true,
  });
}
